import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl, FormControlName, FormGroupDirective, Validators } from '@angular/forms';

import { InputSelectMaterialInterface } from '@shared/components/form/input-select-material/input-select-material.interface';
import { startWith } from 'rxjs';
import { ControlMessage } from '@config/enum/control-message.enum';
import { UtilitiesService } from '@services/utilities.service';
import { STRING_CONSTANTS } from '@config/constants/string.constants';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';

@Component({
  selector: 'app-input-autocomplete',
  templateUrl: './input-autocomplete.component.html',
  styleUrls: ['./input-autocomplete.component.scss'],
})
export class InputAutocompleteComponent implements OnInit, OnChanges {
  @Input() placeholder: string;
  @Input() label: string;
  @Input() required = false;
  @Input() options: InputSelectMaterialInterface[];

  control = new FormControl<string | InputSelectMaterialInterface>('', this.required ? Validators.required : null);
  selectedValue: InputSelectMaterialInterface | null;
  filteredOptions: InputSelectMaterialInterface[];

  controlFormDirective: FormControl;

  ICONS = STRING_CONSTANTS.ICON;
  @ViewChild(MatAutocompleteTrigger) autocomplete: MatAutocompleteTrigger;

  get controlName() {
    return this.controlNameDirective.name;
  }

  get invalidAndDirty() {
    return this.control.invalid && this.control.dirty;
  }

  get hasErrors() {
    return this.control.errors;
  }

  constructor(private controlNameDirective: FormControlName, private formGroupDirective: FormGroupDirective, private utilitiesService: UtilitiesService) {}

  ngOnInit() {
    this.controlFormDirective = this.formGroupDirective.getControl(this.controlNameDirective);
    this.control.valueChanges.pipe(startWith('')).subscribe(() => this.filterOptions());
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.options) {
      this.resetOptions();
    }

    if (changes.required) {
      this.control.setValidators(this.required ? Validators.required : null);
      this.control.updateValueAndValidity();
    }
  }

  displayFn(option: InputSelectMaterialInterface): string {
    if (option && option.code) {
      return option.code + ', ' + option.label;
    }

    return option.label;
  }

  resetSelectedValue() {
    if (!this.filteredOptions) return;

    this.filteredOptions.map((option) => {
      if (option.value === this.selectedValue?.value) {
        option.disabled = false;
      }
    });

    this.selectedValue = null;
  }

  resetOptions() {
    this.filteredOptions = this.options;
  }

  handleOnBlur() {
    // if option already selected and user leaving input field
    // set input value to correct label.
    if (typeof this.control?.value === 'string' && this.selectedValue) {
      this.control.setValue(this.selectedValue);
    }

    // if user entered text on input field without selecting any options
    // set error.
    if (typeof this.control?.value === 'string' && !this.control?.value) {
      this.resetSelectedValue();
      this.control.setErrors({ invalid: true });
    }
  }

  onOptionSelected(event: any) {
    this.selectedValue = event.option.value;
    // emit value to parent form:
    this.controlFormDirective.setValue(this.selectedValue);
  }

  filterOptions() {
    if (!this.hasOptionsData() || typeof this.control?.value !== 'string') {
      return;
    }

    let filterValue = this.normalizeValue(this.control?.value?.toLowerCase() as string);

    let _filteredOptions = this.options.filter((option) => {
      const { label } = option;

      let searchKey = filterValue.split(',')[1];
      searchKey = searchKey ? searchKey : filterValue;

      return this.normalizeValue(label).includes(searchKey);
    });

    return (this.filteredOptions = _filteredOptions);
  }

  normalizeValue(value: string) {
    return value.toLowerCase().replace(/\s/g, '');
  }

  displayErrorMessage(name: any) {
    if (!name) {
      return;
    }

    let _message: string = ' harus diisi';
    _message = this.utilitiesService.mapKeyToString(ControlMessage, name as string) + _message;

    return _message;
  }

  hasOptionsData = () => this.options && !!this.options.length;
}
